import { useEffect, useMemo, useState } from 'react'
import { DatePicker, Select } from 'antd'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import { useSelector } from 'react-redux'
import { ButtonFilledAutoWidth, ButtonOutlinedAutoWidth } from '../../components/button'
import BatchBookingMenu from './BatchBookingMenu'
import { GenerateCustomStudentReportService, GetPersonalTrainingSubscriptionTeacherService } from '../../services/class_booking/classBookingServices'

function BatchCustomDownload() {
  const user = useSelector(state=>state.Auth)
  const isTeacher = user?.logged_user_type === 'Teacher'
  const [loading, setloading] = useState(false)
  const [downloading, setdownloading] = useState(false)
  const [downloadingData, setdownloadingData] = useState({})
  const [batchOptions, setbatchOptions] = useState([])
  const [batchMap, setbatchMap] = useState({})
  const [filters, setfilters] = useState({
    personal_training_subscription_teacher_batch: '',
    student: '',
    start_time: '',
    end_time: '',
    from_date: '',
    to_date: ''
  })

  useEffect(() => {
    getBatchOptions()
  }, [])

  async function getBatchOptions() {
    setloading(true)
    try {
      const response = await GetPersonalTrainingSubscriptionTeacherService({ page: 1, teacher: '' })
      const datas = response?.data?.datas || []

      const options = datas.map((item) => ({
        value: item?._id,
        label: `${item?.teacher?.name || 'Teacher'} | ${item?.class?.label || 'Class'} | ${item?.batch?.start_time || ''} - ${item?.batch?.end_time || ''}`
      }))

      const mapping = {}
      datas.forEach((item) => {
        mapping[item?._id] = item
      })

      setbatchOptions(options)
      setbatchMap(mapping)
    } catch (error) {
      console.error('Error loading batch options:', error)
      toast.error('Unable to load batch options')
    } finally {
      setloading(false)
    }
  }

  function handleChange(value, field) {
    setfilters((prev) => ({ ...prev, [field]: value || '' }))
    setdownloadingData({})
  }

  function resetFilters() {
    setfilters({
      personal_training_subscription_teacher_batch: '',
      student: '',
      start_time: '',
      end_time: '',
      from_date: '',
      to_date: ''
    })
    setdownloadingData({})
  }

  function handleDownload(pathOrUrl = '') {
    if (!pathOrUrl) {
      toast.success('Custom report generated successfully')
      return
    }
    window.open(pathOrUrl, '_blank', 'noopener,noreferrer')
  }

  async function generateReport() {
    if (!filters.personal_training_subscription_teacher_batch) {
      toast.error('Please select a batch')
      return
    }

    if (!filters.student) {
      toast.error('Please select a student')
      return
    }

    if (!filters.start_time || !filters.end_time) {
      toast.error('Please select start and end time')
      return
    }

    if (filters.from_date && filters.to_date && dayjs(filters.from_date).isAfter(dayjs(filters.to_date))) {
      toast.error('From date should be before To date')
      return
    }

    setdownloading(true)
    try {
      const batchId = selectedBatch?.batch?._id || filters.personal_training_subscription_teacher_batch

      const payload = {
        batch: batchId,
        student: filters.student,
        start_time: filters.start_time,
        end_time: filters.end_time,
        from_date: filters.from_date,
        to_date: filters.to_date
      }

      const response = await GenerateCustomStudentReportService(payload)

      if (response?.status === 200) {
        const filePath = response?.data?.path
          || response?.data?.data?.path
          || response?.data?.datas
          || response?.data?.file
          || response?.data?.url

        toast.success(response?.data?.message || 'Custom report generated')
        setdownloadingData({ ...response?.data.report })
        // handleDownload(filePath)
        return
      }

      toast.error(response?.data?.message || 'Failed to generate report')
    } catch (error) {
      console.error('Error generating custom report:', error)
      toast.error('Failed to generate custom report')
    } finally {
      setdownloading(false)
    }
  }

  const selectedBatch = useMemo(() => {
    return batchMap?.[filters.personal_training_subscription_teacher_batch]
  }, [batchMap, filters.personal_training_subscription_teacher_batch])

  const studentOptions = useMemo(() => {
    const students = selectedBatch?.students || []
    return students.map((item) => ({
      value: item?._id,
      label: `${item?.name || 'Student'} ${(!isTeacher && item?.mobile) ? `| ${item.mobile}` : ''}`
    }))
  }, [selectedBatch])

  function handleBatchChange(value) {
    const selected = batchMap?.[value]
    setfilters((prev) => ({
      ...prev,
      personal_training_subscription_teacher_batch: value || '',
      student: '',
      start_time: selected?.batch?.start_time || '',
      end_time: selected?.batch?.end_time || ''
    }))
    setdownloadingData({})
  }

  return (
    <div className='w-[98%] md:h-screen md:overflow-hidden'>
      <div className='md:flex'>
        <div>
          <BatchBookingMenu />
        </div>

        <div className='md:w-[90%] pl-5 mt-4 pr-2'>
          <div className='border-b pb-2'>
            <h6 className='font-[800] text-[14px]'>Custom Download</h6>
            <h6 className='text-[11px] text-slate-500 mt-1'>Select a batch and optional date range, then generate and download the custom report.</h6>
          </div>

          <div className='mt-4 bg-white border rounded-md p-3'>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
              <div>
                <h6 className='text-[11px] mb-1 font-[600]'>Batch</h6>
                <Select
                  value={filters.personal_training_subscription_teacher_batch || undefined}
                  showSearch
                  filterOption
                  allowClear
                  loading={loading}
                  bordered={false}
                  placeholder='Select Batch'
                  onChange={handleBatchChange}
                  options={batchOptions}
                  className='w-full border rounded-[6px] h-[30px] border-slate-300 outline-0 focus:outline-0 focus:ring-0 focus:border-transparent'
                />
              </div>

              <div>
                <h6 className='text-[11px] mb-1 font-[600]'>Student</h6>
                <Select
                  value={filters.student || undefined}
                  showSearch
                  filterOption
                  allowClear
                  bordered={false}
                  disabled={!filters.personal_training_subscription_teacher_batch}
                  placeholder='Select Student'
                  onChange={(value) => handleChange(value, 'student')}
                  options={studentOptions}
                  className='w-full border rounded-[6px] h-[30px] border-slate-300 outline-0 focus:outline-0 focus:ring-0 focus:border-transparent'
                />
              </div>

              <div>
                <h6 className='text-[11px] mb-1 font-[600]'>Start Time</h6>
                <input
                  value={filters.start_time}
                  type='time'
                  onChange={(event) => handleChange(event.target.value, 'start_time')}
                  className='w-full border text-sm h-[30px] py-[4px] focus:ring-0 focus:outline-0 text-[13px] px-2 rounded-md border-slate-300'
                />
              </div>

              <div>
                <h6 className='text-[11px] mb-1 font-[600]'>End Time</h6>
                <input
                  value={filters.end_time}
                  type='time'
                  onChange={(event) => handleChange(event.target.value, 'end_time')}
                  className='w-full border text-sm h-[30px] py-[4px] focus:ring-0 focus:outline-0 text-[13px] px-2 rounded-md border-slate-300'
                />
              </div>

              <div>
                <h6 className='text-[11px] mb-1 font-[600]'>From Date (Optional)</h6>
                <DatePicker
                  size='small'
                  format='YYYY-MM-DD'
                  allowClear
                  placeholder='From Date'
                  value={filters.from_date ? dayjs(filters.from_date) : null}
                  onChange={(_, dateString) => handleChange(dateString, 'from_date')}
                  className='subject-list-date-picker h-[30px] w-full border-slate-300 mr-0'
                />
              </div>

              <div>
                <h6 className='text-[11px] mb-1 font-[600]'>To Date (Optional)</h6>
                <DatePicker
                  size='small'
                  format='YYYY-MM-DD'
                  allowClear
                  placeholder='To Date'
                  value={filters.to_date ? dayjs(filters.to_date) : null}
                  onChange={(_, dateString) => handleChange(dateString, 'to_date')}
                  className='subject-list-date-picker h-[30px] w-full border-slate-300 mr-0'
                />
              </div>
            </div>

            {selectedBatch &&
              <div className='mt-3 border rounded-md bg-slate-50 p-2'>
                <h6 className='text-[11px] font-[700] mb-1'>Selected Batch Preview</h6>
                <h6 className='text-[11px] text-slate-700'>Teacher: {selectedBatch?.teacher?.name || '-'}</h6>
                <h6 className='text-[11px] text-slate-700'>Class: {selectedBatch?.class?.label || '-'}</h6>
                <h6 className='text-[11px] text-slate-700'>Time: {selectedBatch?.batch?.start_time || '-'} - {selectedBatch?.batch?.end_time || '-'}</h6>
                <h6 className='text-[11px] text-slate-700'>Students: {selectedBatch?.students?.length || 0}</h6>
              </div>}

            <div className='mt-4 flex gap-2'>
              <ButtonFilledAutoWidth btnName={downloading ? 'Generating...' : 'Generate Download'} onClick={generateReport} />
              {downloadingData?.url && <ButtonFilledAutoWidth btnName={downloadingData?.url ? 'Download Report' : 'Download Report'} onClick={() => handleDownload(downloadingData?.url)} disabled={!downloadingData?.url} />}
              <ButtonOutlinedAutoWidth btnName='Reset' onClick={resetFilters} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BatchCustomDownload
